import { Check, ChevronDown, ChevronRight, Copy } from "lucide-react";
import React, { useCallback, useState } from "react";
import { graphql, useMutation } from "react-relay";
import config from "src/common/config";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import BitbucketIcon from "src/components/ui/icons/bitbucket";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { SecretInput } from "src/components/ui/secret-input";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import { getProductName } from "src/utils/brandTheme";

import type { BitbucketIntegrationCreateInstallationLinkMutation } from "./__generated__/BitbucketIntegrationCreateInstallationLinkMutation.graphql";

interface BitbucketIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  brandTheme?: string | null;
}

const createBitbucketInstallationLinkMutation = graphql`
  mutation BitbucketIntegrationCreateInstallationLinkMutation($input: CreateBitbucketInstallationLinkInput!) {
    createBitbucketInstallationLink(input: $input) {
      installationLink
    }
  }
`;

export default function BitbucketIntegration({ isOpen, onClose, brandTheme }: BitbucketIntegrationProps) {
  const productName = getProductName(brandTheme);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");
  const { toast } = useToast();

  const [clientKey, setClientKey] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [callbackCopied, setCallbackCopied] = useState(false);
  const [scopeRepositoryWrite, setScopeRepositoryWrite] = useState(true);
  const [scopePullRequestWrite, setScopePullRequestWrite] = useState(true);
  const [scopePipelineWrite, setScopePipelineWrite] = useState(true);

  const callbackUrl = `${config.get("APP_WWW_URL")}/bitbucket/oauth/callback`;
  const handleCopyCallback = useCallback(async () => {
    await navigator.clipboard.writeText(callbackUrl);
    setCallbackCopied(true);
    setTimeout(() => setCallbackCopied(false), 2000);
  }, [callbackUrl]);

  const [commitCreateInstallationLink] = useMutation<BitbucketIntegrationCreateInstallationLinkMutation>(
    createBitbucketInstallationLinkMutation
  );

  const handleConnect = () => {
    if (!clientKey || !clientSecret) {
      toast({ title: "OAuth Consumer Key and Secret are required", variant: "error" });
      return;
    }

    const oauthScopes: string[] = [];
    if (scopeRepositoryWrite) oauthScopes.push("repository:write");
    if (scopePullRequestWrite) oauthScopes.push("pullrequest:write");
    if (scopePipelineWrite) oauthScopes.push("pipeline:write");

    setIsConnecting(true);
    commitCreateInstallationLink({
      variables: {
        input: {
          clientKey,
          clientSecret,
          oauthScopes,
        },
      },
      onCompleted: (response) => {
        setIsConnecting(false);
        const link = response.createBitbucketInstallationLink?.installationLink;
        if (link) {
          window.location.href = link;
        } else {
          toast({
            title: "Failed to generate Bitbucket authorization link",
            description: "No link was returned from the server",
            variant: "error",
          });
        }
      },
      onError: (error) => {
        setIsConnecting(false);
        toast({
          title: "Failed to create Bitbucket connection",
          description: error.message,
          variant: "error",
        });
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl bg-popover text-foreground border-border max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BitbucketIcon className="h-6 w-6" />
            Connect Bitbucket
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Connect {productName} to your Bitbucket Cloud workspace to analyze code and pull requests.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 px-2 space-y-5 pt-2">
          {/* Setup instructions (collapsible) */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setShowInstructions(!showInstructions)}
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              {showInstructions ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {showInstructions ? "Hide instructions" : "Show instructions"}
            </button>

            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
              showInstructions
                ? "max-h-[2000px] opacity-100"
                : "max-h-0 opacity-0"
            }`}>
              <div className="text-muted-foreground space-y-3">
                <p className="mb-2 font-bold">Bitbucket OAuth Consumer Setup:</p>
                <ol className="text-sm space-y-2 list-decimal list-inside">
                  <li>Go to your Bitbucket workspace settings</li>
                  <li>Navigate to <span className="text-amber-600">OAuth consumers</span> under Apps and features</li>
                  <li>Click <span className="text-amber-600">Add consumer</span></li>
                  <li>Check <span className="text-amber-600">This is a private consumer</span></li>
                  <li>Set the callback URL to: <code className="text-green-400 break-all">{callbackUrl}</code>
                    <button type="button" onClick={handleCopyCallback} className="inline-flex items-center ml-2 text-muted-foreground hover:text-foreground transition-colors">
                      {callbackCopied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  </li>
                  <li>Under Permissions, grant the permissions checked below in <span className="text-amber-600">Permissions Granted</span></li>
                  <li>Save and copy the Key and Secret below</li>
                </ol>
              </div>
            </div>
          </div>

          {/* OAuth credentials - side by side */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">OAuth Consumer Key</Label>
              <Input
                type="text"
                autoComplete="off"
                placeholder="OAuth Consumer Key"
                value={clientKey}
                onChange={(e) => setClientKey(e.target.value)}
                className="bg-card border-border text-foreground placeholder:text-muted-foreground/50"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">OAuth Consumer Secret</Label>
              <SecretInput
                placeholder="OAuth Consumer Secret"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                className="bg-card border-border text-foreground placeholder:text-muted-foreground/50"
              />
            </div>
          </div>

          {/* Permissions Granted */}
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Permissions Granted</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Ensure your Bitbucket OAuth consumer is configured with the permissions selected below.
              </p>
              <p className="text-xs text-amber-500 mt-1">
                Changing scopes on the Bitbucket OAuth consumer will revoke access and require you to reconnect the Bitbucket integration.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              {/* Account */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Account</p>
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-not-allowed">
                  <input type="checkbox" checked disabled className="accent-primary" />
                  <span>Read</span>
                  <span className="text-xs">— authentication (required)</span>
                </label>
              </div>

              {/* Repositories */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Repositories</p>
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-not-allowed">
                  <input type="checkbox" checked disabled className="accent-primary" />
                  <span>Read</span>
                  <span className="text-xs">— read code (required)</span>
                </label>
                <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scopeRepositoryWrite}
                    onChange={(e) => setScopeRepositoryWrite(e.target.checked)}
                    className="accent-primary"
                  />
                  <span>Write</span>
                  <span className="text-xs text-muted-foreground">— push code</span>
                </label>
              </div>

              {/* Pull requests */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pull requests</p>
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-not-allowed">
                  <input type="checkbox" checked disabled className="accent-primary" />
                  <span>Read</span>
                  <span className="text-xs">— read PRs (required)</span>
                </label>
                <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scopePullRequestWrite}
                    onChange={(e) => setScopePullRequestWrite(e.target.checked)}
                    className="accent-primary"
                  />
                  <span>Write</span>
                  <span className="text-xs text-muted-foreground">— create PRs</span>
                </label>
              </div>

              {/* Pipelines */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pipelines</p>
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-not-allowed">
                  <input type="checkbox" checked disabled className="accent-primary" />
                  <span>Read</span>
                  <span className="text-xs">— read status/logs (required)</span>
                </label>
                <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scopePipelineWrite}
                    onChange={(e) => setScopePipelineWrite(e.target.checked)}
                    className="accent-primary"
                  />
                  <span>Write</span>
                  <span className="text-xs text-muted-foreground">— trigger pipelines</span>
                </label>
              </div>
            </div>
          </div>

          {/* Connect button */}
          <Tooltip
            content={!canModify ? "You don't have permission to connect integrations" : null}
            position="top"
            asChild
          >
            <span className="block w-full">
              <button
                onClick={handleConnect}
                disabled={isConnecting || !canModify}
                className="w-full bg-[#0052CC] text-white px-4 py-2 rounded-md flex items-center justify-center space-x-2 hover:bg-[#0047B3] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#0052CC]"
              >
                <BitbucketIcon className="h-5 w-5" />
                <span>{isConnecting ? "Loading..." : "Connect to Bitbucket"}</span>
              </button>
            </span>
          </Tooltip>
        </div>
      </DialogContent>
    </Dialog>
  );
}
